import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { useState, useEffect, useRef } from "react";
import {
  GetUrlDocument,
  useSubmitQuestionnaireAnswerMutation,
} from "../../../generated";
import useSnackBars from "../../../hooks/useSnackbar";
import { Chip, IconButton, Switch, Tooltip } from "@mui/material";
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useLazyQuery } from "@apollo/react-hooks";
import Loader from "../../common/Loader";
import useNewrelic from "../../../hooks/useNewrelic";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadFile from "../../../lib/utils/DownloadFile";

interface QuestionProps {
  arn: string;
  createdWorkloads: any;
  index: number;
  row: any;
  updateRow: (row: any, workloadid: string) => void;
  switchBehavior: string;
  questionnaireAnswer: any;
  process: string;
  questionnaireSpinner: any;
  selectedAnswers: string[];
  onUpdateAnswer: (answers: string[]) => void;
  disabledState: boolean[];
  setDisabledState: (updatedArray: boolean[]) => void;
  checkedGenAI: boolean;
}

export default function Question({
  arn,
  createdWorkloads,
  index,
  row,
  updateRow,
  switchBehavior,
  questionnaireAnswer,
  process,
  questionnaireSpinner,
  selectedAnswers,
  onUpdateAnswer,
  disabledState,
  setDisabledState,
  checkedGenAI
}: QuestionProps) {
  const { setAlerts } = useSnackBars();
  const { noticeError } = useNewrelic();
  const [activeIcon, setActiveIcon] = useState(false);
  const [disabledIcon, setDisabledIcon] = useState(false);
  const [processDocName,setProcessDocName] = useState("");
  const [status, setStatus] = useState<string[]>(
    new Array(Object.keys(createdWorkloads).length).fill("INITIAL")
  );

  const [submitQuestionnaireAnswerMutation] =
    useSubmitQuestionnaireAnswerMutation({
      context: {
        apiName: "well_architected",
      },
      notifyOnNetworkStatusChange: true,
  });

  const [ getRoleTemplateURL ] = useLazyQuery(GetUrlDocument,{
    onCompleted: (RoleTemplateURLData) => {
      DownloadFile(RoleTemplateURLData?.getTemplateS3URL, processDocName);
      setDisabledIcon(false);
    }
  });

  useEffect(() => {
    if (row.id === "q7" || row.id === "q12" || row.id === "q22" || row.id === "q29" || row.id === "q37") {
      setActiveIcon(true);
    } else {
      setActiveIcon(false);
    }
  }, [row]);

  const requestQueue = useRef(new Map<number, (() => Promise<void>)[]>()).current;

  const handleQuestionAnswer = (rownumber: number, workloadIndex: number, value: string) => {
    const workloadCount = Object.keys(createdWorkloads).length;

    // 🔁 Disable switches that will be submitted
    const newDisabled = [...disabledState];
    if (switchBehavior === "All") {
      newDisabled.fill(true);
    } else {
      newDisabled[workloadIndex] = true;
    }
    setDisabledState(newDisabled);

    const newStatus = [...status];

    if (switchBehavior === "All") {
      for (let i = 0; i < workloadCount; i++) {
        newStatus[i] = "IN_PROGRESS";
      }
    } else {
      newStatus[workloadIndex] = "IN_PROGRESS";
    }

    setStatus(newStatus);
    // 1️⃣ Build the new answer array
    const newAnswer =
      switchBehavior === "All"
        ? new Array(workloadCount).fill(value)
        : [...selectedAnswers].map((val, i) => (i === workloadIndex ? value : val));

    // 2️⃣ Update the reducer state via callback
    onUpdateAnswer(newAnswer);

    // 3️⃣ Queue backend mutation per workload
    Object.entries(createdWorkloads).forEach(([_, wid], index) => {
      if (switchBehavior === "All" || index === workloadIndex) {
        const submit = async () => {
          try {
            const result = await submitQuestionnaireAnswerMutation({
              variables: {
                arn,
                workLoadId: typeof wid === "string" ? wid : "",
                questionId: row.id,
                questionAnswer: value,
                lensAlias: row.lens,
              },
              context: { apiName: "well_architected" },
            });

            const responseStatus = result?.data?.submitQuestionnaireAnswer?.status;
            const reducedHri = parseInt(result?.data?.submitQuestionnaireAnswer?.result ?? "0");
            // If backend indicates failure, manually handle it
            if (responseStatus !== "COMPLETE") {
              let revertedAnswers = [...selectedAnswers];

              if (switchBehavior === "All") {
                // Revert all to the opposite of the attempted value
                revertedAnswers = new Array(workloadCount).fill(value === "yes" ? "no" : "yes");
              } else {
                // Revert just the failed one
                revertedAnswers[index] = selectedAnswers[index] === "yes" ? "no" : "yes";
              }

              onUpdateAnswer(revertedAnswers);

              // Re-enable switches
              const updatedDisabled = [...disabledState];
              if (switchBehavior === "All") {
                updatedDisabled.fill(false);
              } else {
                updatedDisabled[index] = false;
              }
              setDisabledState(updatedDisabled);

              // Reset status
              const updatedStatus = [...status];
              if (switchBehavior === "All") {
                updatedStatus.fill("INITIAL");
              } else {
                updatedStatus[index] = "INITIAL";
              }
              setStatus(updatedStatus);

              setAlerts([
                {
                  severity: "error",
                  msg: `Question ${rownumber} submission failed.`,
                },
              ]);
            } else {
              const newRow = { ...row };
              newRow.status = value;
              newRow.updateStatus = responseStatus
              newRow.reducedHri = reducedHri;
              const updatedDisabled = [...disabledState];
              updatedDisabled[index] = false;
              setDisabledState(updatedDisabled);
              const updatedStatus = [...status];
              updatedStatus[index] = "INITIAL";
              setStatus(updatedStatus);
              updateRow(newRow, typeof wid === "string" ? wid : "");
            }
          } catch (error) {
            console.error("Submission failed:", error);
            noticeError(error, "Question");
          }
        };

        // Queue logic per workload
        if (!requestQueue.has(index)) {
          requestQueue.set(index, []);
        }
        requestQueue.get(index)?.push(submit);

        if (requestQueue.get(index)?.length === 1) {
          const processQueue = async () => {
            const queue = requestQueue.get(index);
            if (queue?.length) {
              const next = queue.shift();
              if (next) {
                await next();
                if (queue.length) {
                  setTimeout(processQueue, 500);
                }
              }
            }
          };
          processQueue();
        }
      }
    });
  };


  const download = (id: string) => {
    setDisabledIcon(true);
    let templateType = "";
    let fileName = "";

    switch (id) {
      case "q7":
        templateType = "process-documents/AWS-Incident-Management-Process-20240410.docx";
        fileName = "AWS-Incident-Management-Process-20240410.docx";
        break;
      case "q12":
        templateType = "process-documents/AWS-Information-Security-Policy-20240410.docx";
        fileName = "AWS-Information-Security-Policy-20240410.docx";
        break;
      case "q22":
        templateType = "process-documents/Cloud-Disaster-Recovery-Drill-Process-20240424.docx";
        fileName = "Cloud-Disaster-Recovery-Drill-Process-20240424.docx";
        break;
      case "q29":
        templateType = "process-documents/AWS-Decommissioning-Process-20240410.docx";
        fileName = "AWS-Decommissioning-Process-20240410.docx";
        break;
      case "q37":
        templateType = "process-documents/AWS-Cost-Review-Process-for-Workloads-20250408.docx";
        fileName = "AWS-Cost-Review-Process-for-Workloads-20250408.docx";
        break;
      default:
        return;
    }

    setProcessDocName(fileName);

    getRoleTemplateURL({
      variables: {
        template: templateType,
      },
    });

    const newValue = selectedAnswers[0] === "yes" ? "no" : "yes";

    // If the new value is "yes", apply it to all workloads using handleQuestionAnswer
    if (newValue === "yes") {
      handleQuestionAnswer(index, 0, newValue); // this will trigger "All" behavior if switchBehavior === "All"
    }
  };

  return (
    <TableRow
      key={`QuestionTable-${row.id}`}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      <TableCell component="th" scope="row">
        {index}
      </TableCell>
      <TableCell component="th" scope="row" width={"75%"}>
        {row.description}
        {checkedGenAI && row.lens === "wellarchitected" && (
          <Tooltip title="Workload Lens">
            <Chip
              label="AWS Well-Architected Framework"
              size="small"
              sx={{
                backgroundColor: "gray",
                color: "white",
                ml: 0.5,
              }}
            />
          </Tooltip>
        )}
        {checkedGenAI && row.lens === "arn:aws:wellarchitected::aws:lens/genai" && (
          <Tooltip title="Workload Lens">
            <Chip
              label="Generative AI Lens"
              size="small"
              sx={{
                backgroundColor: "gray",
                color: "white",
                ml: 0.5,
              }}
            />
          </Tooltip>
        )}
        { activeIcon ?
            <IconButton onClick={() => download(row.id)} disabled={disabledIcon} type="submit" color="secondary">
              <CloudDownloadOutlinedIcon />
            </IconButton>
          : null
        }
      </TableCell>
      {
        Object.entries(createdWorkloads).map((_, workloadIndex) => {
          const cellKey = `question-${row.id}-workload-${workloadIndex}`;
          if(process === "UPDATE") {
            return questionnaireSpinner === true ? (
              <TableCell align="center" key={cellKey}>
                <Switch
                  color="success"
                  disabled={
                    disabledState[workloadIndex] || status[workloadIndex] === "IN_PROGRESS"
                  }
                  onClick={() => {
                    // Determine the new answer value
                    const newValue = selectedAnswers[workloadIndex] === "yes" ? "no" : "yes";
                    
                    // Create a new answers array that reflects this change:
                    const newAnswer = [...selectedAnswers];
                    newAnswer[workloadIndex] = newValue;
                    
                    // Now call the provided callback to update global answers:
                    onUpdateAnswer(newAnswer);
                    
                    // And then proceed with your existing logic:
                    handleQuestionAnswer(index, workloadIndex, newValue);
                  }}
                  checked={selectedAnswers[workloadIndex] === "yes" || questionnaireAnswer[row.id] === "yes"}
                />
              </TableCell>
            ) : ( 
              <TableCell align="center" key={cellKey}>
                <Loader/>
              </TableCell>
            )
          } else {
            return (
              <TableCell align="center" key={cellKey}>
                <Switch
                  color="success"
                  disabled={disabledState[workloadIndex] || status[workloadIndex] === "IN_PROGRESS"}
                  onClick={() => {
                    // Determine the new answer value
                    const newValue = selectedAnswers[workloadIndex] === "yes" ? "no" : "yes";
                    
                    // Create a new answers array that reflects this change:
                    const newAnswer = [...selectedAnswers];
                    newAnswer[workloadIndex] = newValue;
                    
                    // Now call the provided callback to update global answers:
                    onUpdateAnswer(newAnswer);
                    
                    // And then proceed with your existing logic:
                    handleQuestionAnswer(index, workloadIndex, newValue);
                  }}
                  checked={selectedAnswers[workloadIndex] === "yes"}
                />
              </TableCell>
            )
          }
        })
      }
      <TableCell component="th" scope="row">
        <IconButton
          href={row.info_link}
          target="_blank"
          color="secondary"
        >
          <InfoOutlinedIcon />
        </IconButton>
      </TableCell>
      <TableCell width={"5%"}/>
    </TableRow>
  );
}
